import { Component, Input } from '@angular/core';
import {
  InvoicesTab,
  Page,
  EstimateTab,
  ReportsCommissionsTab,
  ReportsPaymentsTabs,
  ReportsTechPartsTab,
  ScheduleTab,
  Tab,
  ReportsTipsTab,
  ReportsCreditCardTab,
} from '../../models/navigation.model';

type StatusFilter = 'All' | 'Canceled' | 'Pending';

function _scheduleMessage(tab: ScheduleTab, upcoming?: boolean, statusFilter?: StatusFilter) {
  switch (tab) {
    case 'Leads':
      return upcoming ? 'You have no upcoming leads' : 'No leads in your schedule';
    case 'All':
    case 'Unavailable':
      return `Your ${upcoming ? 'upcoming ' : ''}schedule is empty`;
    case 'Assessments':
      if (statusFilter === 'Canceled') {
        return upcoming ? 'You have no canceled assessments in upcoming' : 'You have no canceled assessments';
      } else {
        return upcoming ? 'You have no upcoming assessments' : 'No assessments in your schedule';
      }
    case 'Jobs':
      if (statusFilter === 'Canceled') {
        return upcoming ? 'Upcoming schedule has no job cancellations' : 'No jobs have been canceled';
      }
      if (statusFilter === 'Pending') {
        return upcoming ? 'Upcoming schedule has no pending jobs' : 'No pending jobs';
      }
      return upcoming ? 'You have no upcoming jobs' : 'No jobs in your schedule';
  }
}

function _estimatesMessage(tab: EstimateTab) {
  return {
    'Won': "No estimates won yet",
    'Pending': "No pending estimates",
    'Declined': "No estimates declined",
    'Canceled': "No estimates canceled",
    'Drafts': "No estimates in drafts",
  }[tab]!;
}

function _invoicesMessage(tab: InvoicesTab) {
  return {
    'Paid': "No paid invoices yet",
    'Pending': "No pending invoices",
    'Canceled': "No canceled invoices",
    'Drafts': "No invoices in drafts",
  }[tab]!;
}

function _reportsPaymentsMessage(tab: ReportsPaymentsTabs) {
  return {
    'All': 'No payments activity',
    'Paid': 'No paid payments',
    'Pending': 'No pending payments'
  }[tab]!;
}

function _reportsCommissionsMessage(tab: ReportsCommissionsTab) {
  return {
    'Pending': 'No pending commissions',
    'Paid': 'No paid commissions'
  }[tab]!;
}

function _reportsTechPartsMessage(tab: ReportsTechPartsTab) {
  return {
    'Pending': 'No pending tech parts',
    'Paid': 'No paid tech parts'
  }[tab]!;
}

function _reportsTipsMessage(tab: ReportsTipsTab) {
  return {
    'Pending': 'No pending tips',
    'Paid': 'No paid tips'
  }[tab]!;
}

function _reportsCreditCardMessage(tab: ReportsCreditCardTab) {
  return {
    'Settled': 'No settled payments',
    'Pending': 'No pending payments',
    'Pre-chargebacks': 'No pre-chargebacks',
    'Chargebacks': 'No chargebacks',
    'Refunds': 'No refunds',
    'Surcharge': 'No money saved'
  }[tab]!;
}

@Component({
  selector: 'app-empty-state',
  templateUrl: './empty-state.component.html',
  styleUrls: ['./empty-state.component.scss']
})
export class EmptyStateComponent {

  @Input() filter?: 'Search' | 'Range';
  @Input() page?: Exclude<Page, 'Settings'>;
  @Input() tab?: Tab;
  @Input() statusFilter?: StatusFilter;
  @Input() upcoming?: boolean;

  constructor() {
  }

  getMessage() {
    if (this.filter) {
      if (this.page === 'Items') {
        return "No items found";
      }

      return {
        'Search': "No results found",
        'Range': "No results for\nthis time range",
      }[this.filter]!;
    }
    switch (this.page) {
      case 'Schedule':
        return _scheduleMessage(this.tab as ScheduleTab, this.upcoming, this.statusFilter);
      case 'Estimates':
        return _estimatesMessage(this.tab as EstimateTab);
      case 'Invoices':
        return _invoicesMessage(this.tab as InvoicesTab);
      case 'Transactions':
        return _reportsPaymentsMessage(this.tab as ReportsPaymentsTabs);
      case 'Commissions':
        return _reportsCommissionsMessage(this.tab as ReportsCommissionsTab);
      case 'Tech Parts':
        return _reportsTechPartsMessage(this.tab as ReportsTechPartsTab);
      case 'Tips':
        return _reportsTipsMessage(this.tab as ReportsTipsTab);
      case 'Credit Card':
        return _reportsCreditCardMessage(this.tab as ReportsCreditCardTab);
      case 'Assigned':
        return 'There are no pending assignments from you'
      case 'Assignment':
        return 'There are no pending assignments for you'
      case 'Items':
        return 'No items added yet';
    }

    throw `${this.page} is not set up for empty state`;
  }

}
